import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { authService } from '../services/auth';
import { emailService } from '../services/email';

// BAFS Department types
export type BAFSDepartment = 'cet' | 'finance' | 'operations' | 'administration';

export type BAFSRole = 
  | 'cet_manager' 
  | 'finance_manager' 
  | 'ops_manager' 
  | 'administrator';

// Permission types
export type Permission = 
  | 'clients.read' | 'clients.write'
  | 'communications.read' | 'communications.write'
  | 'documents.read' | 'documents.write'
  | 'features.read' | 'features.write'
  | 'invoices.read' | 'invoices.write'
  | 'statements.read' | 'statements.write'
  | 'receivables.read' | 'receivables.write'
  | 'products.read' | 'products.write'
  | 'pricing.read' | 'pricing.write'
  | 'client_pricing.read' | 'client_pricing.write'
  | 'financials.read' | 'financials.write'
  | 'sales.read' | 'sales.write'
  | 'cases.read' | 'cases.write';

// User interface
export interface User {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  departments: BAFSDepartment[];
  primaryDepartment: BAFSDepartment;
  roles: BAFSRole[];
  primaryRole: BAFSRole;
  permissions: Permission[];
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEPARTMENT_CONFIG: Record<BAFSDepartment, {
  label: string;
  role: BAFSRole;
  permissions: Permission[];
}> = {
  cet: {
    label: 'Client Experience Team',
    role: 'cet_manager',
    permissions: [
      'clients.read', 'clients.write',
      'communications.read', 'communications.write',
      'documents.read', 'documents.write',
      'features.read', 'features.write',
      'invoices.read',
      'statements.read',
      'receivables.read',
      'products.read', 'products.write',
      'pricing.read',
      'client_pricing.read', 'client_pricing.write',
      'sales.read', 'sales.write',
      'financials.read', 'financials.write',
      'cases.read', 'cases.write',
    ],
  },
  finance: {
    label: 'Finance',
    role: 'finance_manager',
    permissions: [
      'clients.read',
      'communications.read',
      'documents.read', 'documents.write',
      'invoices.read', 'invoices.write',
      'statements.read', 'statements.write',
      'receivables.read', 'receivables.write',
      'products.read',
      'pricing.read',
      'client_pricing.read',
      'financials.read', 'financials.write',
    ],
  },
  operations: {
    label: 'Operations',
    role: 'ops_manager',
    permissions: [
      'clients.read',
      'documents.read',
      'products.read', 'products.write',
      'pricing.read', 'pricing.write',
      'client_pricing.read', 'client_pricing.write',
      'invoices.read',
    ],
  },
  administration: {
    label: 'Administration',
    role: 'administrator',
    permissions: [
      'clients.read',
      'communications.read',
      'features.read',
      'invoices.read',
      'statements.read',
      'receivables.read',
      'products.read',
      'pricing.read',
      'client_pricing.read',
    ],
  },
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpiresAt: Date | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-read the stored session and load the user. The auth callback page calls
   * this after exchanging its login code, so the provider learns about the new
   * session before the app navigates into a protected route.
   */
  refreshAuth: () => Promise<void>;
  extendSession: () => Promise<boolean>;
  hasPermission: (permission: Permission) => boolean;
  canWrite: (resource: string) => boolean;
  canRead: (resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token refresh interval (4 minutes - refresh before 5 minute threshold)
const TOKEN_REFRESH_INTERVAL = 4 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = user !== null;

  // Refresh tokens
  const refreshTokens = useCallback(async () => {
    const result = await authService.refreshTokens();
    
    if (!result.success) {
      // Only log out for actual session expiry, not missing refresh token
      // (MSAL manages refresh tokens internally, so NO_REFRESH_TOKEN is normal)
      if (result.code === 'SESSION_EXPIRED') {
        console.warn('Session expired, logging out');
        setUser(null);
        setSessionExpiresAt(null);
        authService.clearSessionId();
        return false;
      }
      
      // For NO_REFRESH_TOKEN or REFRESH_FAILED, just log and continue
      // The session is still valid, we just can't silently refresh Azure tokens
      if (result.code !== 'NO_SESSION') {
        console.debug('Token refresh skipped:', result.code);
      }
      return false;
    }
    
    // Track session expiry from server response
    if (result.sessionExpiresAt) {
      setSessionExpiresAt(result.sessionExpiresAt);
    }
    
    if (result.refreshed) {
      console.log('Tokens refreshed, expires in:', result.expiresIn, 'seconds');
    }
    return true;
  }, []);

  // Start token refresh interval
  const startRefreshInterval = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Set up new interval
    refreshIntervalRef.current = setInterval(refreshTokens, TOKEN_REFRESH_INTERVAL);
    console.log('Token refresh interval started');
  }, [refreshTokens]);

  // Stop token refresh interval
  const stopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      console.log('Token refresh interval stopped');
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
    
    // Cleanup on unmount
    return () => {
      stopRefreshInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopRefreshInterval]);

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      const status = await authService.checkStatus();
      if (status.authenticated && status.user) {
        // Fetch full profile
        const profile = await authService.getProfile();
        setUser(profile);
        
        // Start token refresh interval
        startRefreshInterval();
        
        // Do an immediate refresh check
        refreshTokens();
        
        // Trigger background email sync (fire-and-forget)
        // This syncs any replies to tracked email conversations
        emailService.syncEmails().then((result) => {
          if (result.success) {
            console.log('Email sync completed:', {
              conversations: result.conversationsSynced,
              newMessages: result.newMessagesFound,
            });
          } else if (result.error) {
            console.debug('Email sync skipped:', result.error);
          }
        }).catch((error) => {
          // Non-fatal - don't interrupt the user
          console.debug('Email sync error:', error);
        });
      } else {
        setUser(null);
        stopRefreshInterval();
      }
    } catch (error) {
      console.error('Failed to check auth status', error);
      setUser(null);
      stopRefreshInterval();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    setIsLoading(true);
    try {
      // Get current path for redirect after login
      const redirectUrl = window.location.pathname + window.location.search;
      
      // Initiate Azure SSO login
      const response = await authService.initiateLogin(redirectUrl);
      
      // Store state for validation on callback
      sessionStorage.setItem('oauth_state', response.state);
      
      // Redirect to Azure
      window.location.href = response.authorizationUrl;
    } catch (error) {
      console.error('Failed to initiate login', error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    stopRefreshInterval();
    try {
      // Clear only the Slope session, not Azure SSO
      await authService.logout({ logoutFromAzure: false });
      setUser(null);
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to logout', error);
      setUser(null);
      // Still redirect to home on error
      window.location.href = '/';
    }
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const canWrite = (resource: string): boolean => {
    return hasPermission(`${resource}.write` as Permission);
  };

  const canRead = (resource: string): boolean => {
    return hasPermission(`${resource}.read` as Permission) || 
           hasPermission(`${resource}.write` as Permission);
  };

  // Force refresh tokens and extend session (used by session timeout warning)
  const extendSession = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.forceRefreshAndExtend();
      
      if (!result.success) {
        console.warn('Failed to extend session:', result.code);
        return false;
      }
      
      // Update session expiry from response
      if (result.sessionExpiresAt) {
        setSessionExpiresAt(result.sessionExpiresAt);
      }
      
      console.log('Session extended successfully', {
        sessionExpiresAt: result.sessionExpiresAt,
        tokenExpiresIn: result.expiresIn,
      });
      
      return true;
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        sessionExpiresAt,
        login,
        logout,
        refreshAuth: checkAuth,
        extendSession,
        hasPermission,
        canWrite,
        canRead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hook for permission checks
// eslint-disable-next-line react-refresh/only-export-components
export function usePermission(permission: Permission): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

// Helper hook for resource access
// eslint-disable-next-line react-refresh/only-export-components
export function useResourceAccess(resource: string) {
  const { canRead, canWrite } = useAuth();
  return {
    canRead: canRead(resource),
    canWrite: canWrite(resource),
  };
}
